import { Button } from '@/components/ui/button';
import { Target, Flame } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { useIsMobile } from '@/hooks/use-mobile';
import foxThumbsUp from '@/assets/fox-thumbs-up.webp';

interface PostingGoalCardProps {
  current: number;
  goal: number;
  timePeriod: 'week' | 'month';
}

const SegmentedProgress = ({ current, goal, height = 'h-3' }: { current: number; goal: number; height?: string }) => {
  const segments = goal;
  
  return (
    <div className={`flex gap-0.5 w-full ${height}`}>
      {Array.from({ length: segments }).map((_, index) => {
        const isFilled = index < current;
        
        return (
          <div 
            key={index} 
            className="flex-1 bg-white/30 dark:bg-white/20 rounded-sm overflow-hidden"
          >
            <div 
              className={`h-full transition-all ${isFilled ? 'bg-gradient-to-r from-orange-500 to-orange-600 w-full' : 'w-0'}`}
            />
          </div>
        );
      })}
    </div>
  );
};

const PostingGoalCard = ({ current, goal, timePeriod }: PostingGoalCardProps) => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const isMobile = useIsMobile();
  const remaining = Math.max(0, goal - current);
  const percentage = Math.min(100, Math.round((current / goal) * 100));
  
  // Show thumbs up when weekly >= 3 or monthly >= 12
  const showThumbsUp = timePeriod === 'week' ? current >= 3 : current >= 12;
  
  const goalLabel = timePeriod === 'week' 
    ? t('analytics.weeklyGoal')
    : t('analytics.monthlyGoal');
  
  return (
    <div className="relative overflow-visible shadow-md transition-all h-auto animate-fade-in rounded-lg">
      {/* Gradient background */}
      <div className="absolute inset-0 bg-gradient-to-r from-blue-500 via-blue-600 to-emerald-500 rounded-lg" />
      
      {/* Fox thumbs up - absolute positioned at bottom of box, next to progress bar */}
      {showThumbsUp && (
        <img 
          src={foxThumbsUp} 
          alt="Goal exceeded!" 
          className="absolute bottom-0 right-[200px] md:right-[280px] h-14 md:h-16 object-contain z-10 animate-fade-in"
        />
      )}
      
      {/* Desktop layout */}
      <div className="relative px-4 md:px-6 py-2.5 md:py-3 items-center justify-between gap-2 md:gap-4 hidden md:flex">
        {/* Left: Title and Stats */}
        <div className="flex items-center gap-2 md:gap-4 flex-shrink-0">
          <div className="flex items-center gap-1.5 md:gap-2">
            <Target className="h-4 w-4 md:h-5 md:w-5 text-white flex-shrink-0" />
            <span className="text-white font-bold text-xs md:text-base whitespace-nowrap">{goalLabel}</span>
          </div>
          
          <div className="flex items-center gap-1.5 md:gap-2 text-white/90 text-xs md:text-sm">
            <span className="font-semibold">{current}/{goal}</span>
            <span>•</span>
            <span>{percentage}%</span>
          </div>
        </div>

        {/* Center: Segmented Progress Bar */}
        <div className="flex-1 max-w-md px-4">
          <SegmentedProgress current={current} goal={goal} height="h-3" />
        </div>

        {/* Right: CTA */}
        <Button 
          onClick={() => navigate('/biblioteka')}
          size="sm"
          className="bg-white dark:bg-white/10 dark:text-white hover:bg-white/90 dark:hover:bg-white/20 text-blue-600 font-semibold h-7 md:h-8 px-2 md:px-3 text-xs whitespace-nowrap flex-shrink-0"
        >
          <Flame className="mr-1 h-3 w-3" />
          <span>{t('dashboard.getIdeas')}</span>
        </Button>
      </div>

      {/* Mobile layout */}
      <div className="relative px-4 py-2.5 md:hidden">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5">
            <Target className="h-4 w-4 text-white flex-shrink-0" />
            <span className="text-white font-bold text-xs whitespace-nowrap">{goalLabel}</span>
          </div>

          <div className="flex items-center gap-1.5 text-white/90 text-xs">
            <span className="font-semibold">{current}/{goal}</span>
          </div>

          <Button
            onClick={() => navigate('/biblioteka')}
            size="sm"
            className="bg-white dark:bg-white/10 dark:text-white hover:bg-white/90 dark:hover:bg-white/20 text-blue-600 font-semibold h-7 px-2 text-xs whitespace-nowrap flex-shrink-0"
          >
            <Flame className="mr-1 h-3 w-3" />
            <span>{t('dashboard.getIdeas')}</span>
          </Button>
        </div>
        
        {/* Segmented Progress Bar */}
        <div className="mt-2">
          <SegmentedProgress current={current} goal={goal} height="h-2" />
        </div>
      </div>
    </div>
  );
};

export default PostingGoalCard;
